import { Autocomplete, FormControl, TextField } from '@mui/material';
import { startTransition, useCallback, useOptimistic } from 'react';
import { useSearchParams } from 'react-router';
import z from 'zod';

import { drawerZIndex } from '../../components/filter/FilterDrawer';
import { FilterSelectedLabel } from '../../components/filter/filterUtils';
import { type Geschaeftsbereich, geschaeftsbereiche } from '../../dtos';

export const geschaeftsbereichFilterKey = 'bu';

export function useGeschaeftsbereichFilter() {
  const [searchParams, setSearchParams] = useSearchParams();

  const actualValue = z
    .array(z.enum(geschaeftsbereiche))
    .parse(JSON.parse(searchParams.get(geschaeftsbereichFilterKey) ?? '[]'));

  const [optimisticGeschaeftsbereichFilter, setOptimisticGeschaeftsbereichFilter] = useOptimistic(
    actualValue,
    (_, newValue: Geschaeftsbereich[]) => newValue,
  );

  const setGeschaeftsbereichFilter = useCallback(
    (value: Geschaeftsbereich[]) => {
      startTransition(() => {
        setOptimisticGeschaeftsbereichFilter(value);
        setSearchParams(
          // The prev parameter from react-router may be stale, see https://github.com/remix-run/react-router/issues/9757
          () => {
            const prev = new URLSearchParams(window.location.search);
            if (value.length === 0) {
              prev.delete(geschaeftsbereichFilterKey);
            } else {
              prev.set(geschaeftsbereichFilterKey, JSON.stringify(value));
            }
            return prev;
          },
          { replace: true },
        );
      });
    },
    [setSearchParams, setOptimisticGeschaeftsbereichFilter],
  );

  return [optimisticGeschaeftsbereichFilter, setGeschaeftsbereichFilter] as const;
}

type GeschaeftsbereichFilterProps = {
  readonly inDrawer?: boolean;
};

export const GeschaeftsbereichFilter = ({ inDrawer }: GeschaeftsbereichFilterProps) => {
  const [geschaeftsbereichFilter, setGeschaeftsbereichFilter] = useGeschaeftsbereichFilter();
  return (
    <FormControl sx={{ width: inDrawer ? '100%' : '12rem' }}>
      <Autocomplete
        id="geschaeftsbereich-input"
        size="small"
        multiple
        value={geschaeftsbereichFilter}
        onChange={(_, newValues) => {
          setGeschaeftsbereichFilter(newValues);
        }}
        options={Object.values(geschaeftsbereiche)}
        limitTags={0}
        getLimitTagsText={FilterSelectedLabel}
        slotProps={{ popper: { sx: { zIndex: (theme) => drawerZIndex(theme) + 1 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Geschäftsbereich"
            placeholder="Geschäftsbereich"
            sx={{
              '& input': {
                display: () => (geschaeftsbereichFilter.length > 0 ? 'none' : undefined),
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};
